import { forwardRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, X } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { AdminWalletTopUpRequestItem, StoreSubscriptionPaymentReviewStatus } from "@/lib/api/types";
import { STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { formatDateTime } from "@/lib/formatting/date-time";
import {
  useConfirmStoreSubscriptionTopUpMutation,
  useRejectStoreSubscriptionTopUpMutation,
} from "@/shared/stores/services/mutations/useReviewStoreSubscriptionTopUpMutation";
import { SubscriptionPaymentProofModal } from "@/shared/stores/components/subscriptions/SubscriptionPaymentProofModal";
import { Badge, Banner, Button, Field, Icon, Input, Modal, StatusDot, useToast, type DotTone } from "@/shared/rd";

/** A rejection reason shorter than this is not a reason. Mirrors the DTO. */
const MIN_REASON_LENGTH = 2;

const STATUS: Record<StoreSubscriptionPaymentReviewStatus, { tone: DotTone; label: string }> = {
  [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING]: { tone: "blue", label: keys.admin.subscriptionTopUpStatusPending },
  [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.CONFIRMED]: { tone: "green", label: keys.admin.subscriptionTopUpStatusConfirmed },
  [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.REJECTED]: { tone: "red", label: keys.admin.subscriptionTopUpStatusRejected },
};

type Props = {
  item: AdminWalletTopUpRequestItem;
  locale: string;
  highlighted: boolean;
  /** Confirm/reject are managing-admin work; others only read the queue. */
  canReview: boolean;
};

/**
 * One wallet top-up request. Confirm is the only path that moves money (the
 * backend credits the wallet and releases held orders oldest-first in one
 * transaction). Reject asks for a REQUIRED reason in a modal: it is sent to
 * the seller and is the only way they learn what to fix. The API has no way
 * to undo a rejection, so it stays behind that modal rather than an Undo.
 * A success is a shell toast; a failure is a Banner on this card (or in the
 * modal) with Try again, never a toast.
 */
export const TopUpRequestCard = forwardRef<HTMLElement, Props>(function TopUpRequestCard(
  { item, locale, highlighted, canReview },
  ref,
) {
  const t = useTranslations();
  const toast = useToast();
  const navigate = useNavigate();
  /** The failed confirm's server detail ("" when it had none). */
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const confirm = useConfirmStoreSubscriptionTopUpMutation();
  const reject = useRejectStoreSubscriptionTopUpMutation();
  const busy = confirm.isPending || reject.isPending;

  const isPending = item.reviewStatus === STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING;
  const status = STATUS[item.reviewStatus];

  /** The server's own words when it gave any; the banner says the rest. */
  const toDetail = (caught: unknown) => (caught instanceof ApiJsonClientError ? caught.message || "" : "");
  const storePath = `/${locale}/admin/stores/${item.storeId}?tab=subscriptions`;

  const amount = (() => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: item.currency || "EGP",
        maximumFractionDigits: 2,
      }).format(item.amount);
    } catch {
      return `${item.amount} ${item.currency}`.trim();
    }
  })();

  const handleConfirm = async () => {
    setError(null);
    try {
      await confirm.mutateAsync({ storeId: item.storeId, paymentId: item.id });
      toast.show({
        message: t("admin.walletTopUpAddedToast", { amount, store: item.storeName }),
        actionLabel: t(keys.common.view),
        onAction: () => navigate(storePath),
      });
    } catch (caught) {
      setError(toDetail(caught));
    }
  };

  const handleReject = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < MIN_REASON_LENGTH) {
      setReasonError(t(keys.admin.subscriptionTopUpRejectReasonRequired));
      return;
    }
    setReasonError(null);
    setRejectError(null);
    try {
      await reject.mutateAsync({ storeId: item.storeId, paymentId: item.id, reason: trimmed });
      setRejecting(false);
      setReason("");
      toast.show({ message: t("admin.walletTopUpRejectedToast", { store: item.storeName }) });
    } catch (caught) {
      setRejectError(toDetail(caught));
    }
  };

  const reviewedOn = formatDateTime(item.reviewedAt, locale);

  return (
    <article
      ref={ref}
      className="rd-card"
      data-payment-id={item.id}
      style={{
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        boxShadow: highlighted ? "0 0 0 2px var(--rd-primary)" : undefined,
      }}
    >
      {highlighted ? (
        <span
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 500,
            padding: "2px 8px",
            borderRadius: 10,
            background: "var(--rd-blue-tag)",
            color: "var(--rd-blue-tag-ink)",
          }}
        >
          <Icon icon={Bell} size={12} />
          {t(keys.admin.walletTopUpQueueFromNotification)}
        </span>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Badge id="currency" size={36} shape="rounded" />
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, flex: 1, minWidth: 200 }}>
          <span className="rd-num" style={{ fontSize: 20, fontWeight: 700 }}>
            {amount}
          </span>
          <Link
            to={`/${locale}/admin/stores/${item.storeId}?tab=subscriptions`}
            style={{ fontSize: 13, color: "var(--rd-primary)", alignSelf: "flex-start" }}
            dir="auto"
          >
            {item.storeName}
          </Link>
        </div>
        <StatusDot tone={status.tone}>{t(status.label)}</StatusDot>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: "10px 24px" }}>
        <dl className="rd-kv" style={{ gridTemplateColumns: "120px minmax(0,1fr)", fontSize: 13 }}>
          <dt>{t(keys.admin.subscriptionTopUpRequestedBy)}</dt>
          <dd dir="auto">{item.requestedBySeller?.name ?? "—"}</dd>
          <dt>{t(keys.admin.subscriptionTopUpRequestedAt)}</dt>
          <dd>{formatDateTime(item.requestedAt, locale)}</dd>
        </dl>
        <dl className="rd-kv" style={{ gridTemplateColumns: "120px minmax(0,1fr)", fontSize: 13 }}>
          <dt>{t(keys.admin.walletTopUpQueueReviewedAt)}</dt>
          <dd>{reviewedOn}</dd>
          <dt>{t(keys.admin.subscriptionPaymentProof)}</dt>
          <dd>
            {item.hasProof ? (
              <SubscriptionPaymentProofModal storeId={item.storeId} paymentId={item.id} />
            ) : (
              <span style={{ color: "var(--rd-text-meta)" }}>{t(keys.admin.subscriptionPaymentProofMissing)}</span>
            )}
          </dd>
        </dl>
      </div>

      {item.reviewReason ? (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: "var(--rd-radius-lg)",
            background: "var(--rd-red-tag)",
            color: "var(--rd-red-tag-ink)",
            fontSize: 13,
          }}
        >
          <b style={{ fontWeight: 600 }}>{t(keys.admin.subscriptionTopUpRejectReasonLabel)}:</b>{" "}
          <span dir="auto">{item.reviewReason}</span>
        </div>
      ) : null}

      {isPending && canReview ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {error !== null ? (
            <Banner
              tone="error"
              title={t("admin.walletTopUpConfirmFailedTitle")}
              actions={
                <Button
                  size="sm"
                  loading={confirm.isPending}
                  loadingLabel={t(keys.shell.retrying)}
                  onClick={() => void handleConfirm()}
                >
                  {t(keys.shell.retry)}
                </Button>
              }
            >
              {[error, t("admin.walletTopUpConfirmFailedBody")].filter(Boolean).join(" ")}
            </Banner>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              variant="primary"
              size="sm"
              icon={Check}
              disabled={reject.isPending}
              loading={confirm.isPending}
              loadingLabel={t("admin.walletTopUpConfirming")}
              onClick={() => void handleConfirm()}
            >
              {t(keys.admin.subscriptionTopUpConfirmAction)}
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={X}
              disabled={busy}
              onClick={() => {
                setReason("");
                setReasonError(null);
                setRejectError(null);
                setRejecting(true);
              }}
            >
              {t(keys.admin.subscriptionTopUpRejectAction)}
            </Button>
          </div>
        </div>
      ) : null}

      <Modal
        open={rejecting}
        onClose={busy ? undefined : () => setRejecting(false)}
        title={t("admin.walletTopUpRejectTitle")}
        width={440}
        actions={
          <>
            <Button variant="ghost" muted disabled={busy} onClick={() => setRejecting(false)}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="danger"
              loading={reject.isPending}
              loadingLabel={t("admin.walletTopUpRejecting")}
              onClick={() => void handleReject()}
            >
              {t(rejectError !== null ? keys.shell.retry : "admin.walletTopUpRejectSubmit")}
            </Button>
          </>
        }
      >
        {rejectError !== null ? (
          <Banner tone="error" title={t("admin.walletTopUpRejectFailedTitle")}>
            {[rejectError, t("admin.walletTopUpRejectFailedBody")].filter(Boolean).join(" ")}
          </Banner>
        ) : null}
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t("admin.walletTopUpRejectBody")}
        </p>
        <Field label={t(keys.admin.subscriptionTopUpRejectReasonLabel)} error={reasonError} htmlFor={`reject-${item.id}`}>
          <Input
            id={`reject-${item.id}`}
            name="reason"
            autoFocus
            value={reason}
            invalid={Boolean(reasonError)}
            placeholder={t(keys.admin.subscriptionTopUpRejectReasonPlaceholder)}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleReject();
            }}
          />
        </Field>
      </Modal>
    </article>
  );
});
